/**
 * Integrytas Group - Common Security Module (CSM)
 * Page: CSM.USER.PROVISION
 *
 * User-provisioning workspace driven by csm_job_description.approver chain.
 *
 * Tabs:
 *   - "Submit New User"   : a form for the requester (any authenticated user)
 *                           that captures target email/name/locale + job
 *                           description + roles + business reason. Submitting
 *                           calls csm_provision_submit, which opens an APR request that
 *                           the request, the requested roles, and queues an
 *                           email to the first approver.
 *   - "Pending My Approval": rows where current_approver_id = me. Opening a
 *                           row shows requested roles with per-role
 *                           Approve/Reject toggles + an overall comment.
 *                           Submitting calls apr_decide_lines
 *                           which advances or finalizes the workflow.
 *   - "My Submissions"     : requests I requested, any status.
 *
 * Forensic stamping: every write goes through getForensicActorId(session)
 * so mimic sessions log the real admin.
 */
/* tl-group-ok: the listed row is a provision request, which has no _tl. The embedded
   csm_job_description_tl / csm_role_master_tl resolve the names of the roles and job being
   REQUESTED (§17.B4) — they are not translations of the request itself. */

/* rbac-affordance-ok: neither write is page-scoped. csm_provision_submit
   is a REQUEST any authenticated user may raise (it creates nothing until
   signed off), and apr_decide_lines delegates to
   csm_apply_signoff_internal, which refuses anyone who is not the request's
   current_approver_id (CSM.PROVISION.NOT_YOUR_TURN). */
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { useActiveLocale } from '../../lib/use_active_locale';
import { apiDb } from '../../lib/api_client';
import { getForensicActorId } from '../../types/auth';
import type { AuthSession } from '../../types/auth';
import { FilteredModalSingleSelect } from '../../components/common/FilteredModalSingleSelect';
import { LocaleSelect } from '../../components/common/LocaleSelect';
import { Modal } from '../../components/common/Modal';
import { SortableHeader } from '../../components/common/SortableHeader';
import { compareStrings, useSort } from '../../lib/use_sort';
import { useOrgLookups } from '../../lib/use_org_lookups';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid, PersonalizedField, GRID_TABLE_CLASS } from '../../components/personalization';
import { usePersonalization } from '../../lib/use_personalization';
import type { GridColumnDef } from '../../components/personalization';
import { FieldRenderer, toUldCoreLayout, type FieldDef, type SectionDef } from '../../components/common/FieldRenderer';
import { useUldSectionedLayout } from '../../hooks/use_uld_layout';
import { FacetFilter } from '../../components/common/FacetFilter';
import { ClearFiltersButton } from '../../components/common/ClearFiltersButton';
import { StatusBadge, type StatusVariant } from '../../components/common/StatusBadge';
import { ErrorBanner } from '../../components/common/ErrorBanner';
import { SkeletonRows } from '../../components/common/SkeletonRows';
import { EmptyState } from '../../components/common/EmptyState';
import { fetchAllJobDefaultRoles } from '../../lib/job_default_roles';
import { RoleReconciliationModal } from '../../components/common/RoleReconciliationModal';
import { applyRoleStrategy } from '../../lib/role_reconcile';

type RequestSortKey =
  | 'request_internal_code' | 'target_email' | 'target_display_name'
  | 'target_job_label' | 'status_code' | 'current_sequence_order'
  | 'current_approver_label';

type Tab = 'submit' | 'pending' | 'mine';

interface JobOpt {
  job_description_id: number;
  internal_code: string;
  display_text: string;
  approver_count: number;
}

interface RoleOpt {
  role_id: number;
  internal_code: string;
  display_text: string;
}

interface RequestRow {
  /** apr_request.approval_request_id — provisioning is an APR request now. */
  approval_request_id: number;
  request_internal_code: string;
  target_email: string;
  target_display_name: string;
  target_job_label: string;
  requested_by: number;
  requester_label: string;
  status_code: string;
  status_label: string;
  current_sequence_order: number;
  current_approver_id: number | null;
  current_approver_label: string | null;
  business_reason: string | null;
  submitted_at: string | null;
  finalized_at: string | null;
}

const FILTER_KEY = 'csm.provision.filter';

const GRID_COLS: GridColumnDef[] = [
  { key: 'request_internal_code',  header: 'Request',   defaultWidth: 160 },
  { key: 'target_email',           header: 'Email',     defaultWidth: 200 },
  { key: 'target_display_name',    header: 'Name',      defaultWidth: 160 },
  { key: 'target_job_label',       header: 'Job',       defaultWidth: 150 },
  { key: 'status_code',            header: 'Status',    defaultWidth: 110 },
  { key: 'current_sequence_order', header: 'Step',      defaultWidth: 70  },
  { key: 'current_approver_label', header: 'Awaiting',  defaultWidth: 160 },
];

// ULD section-aware field layout for the whole Submit-New-User form.
const PROVISION_SECTIONS: SectionDef[] = [
  { key: 'identity', label: 'Identity' },
  { key: 'org',      label: 'Organization' },
  { key: 'request',  label: 'Request Details' },
];
const PROVISION_FIELD_DEFS: FieldDef[] = [
  { key: 'target_email',     type: 'text',     label: 'Target Email',     section: 'identity' },
  { key: 'display_name',     type: 'text',     label: 'Display Name',     section: 'identity' },
  { key: 'preferred_locale', type: 'text',     label: 'Preferred Locale', section: 'identity' },
  { key: 'job_description',  type: 'custom',   label: 'Job Description',   section: 'identity' },
  { key: 'company',          type: 'custom',   label: 'Company',          section: 'org' },
  { key: 'division',         type: 'custom',   label: 'Division',         section: 'org' },
  { key: 'business_unit',    type: 'custom',   label: 'Business Unit',     section: 'org' },
  { key: 'office',           type: 'custom',   label: 'Office',            section: 'org' },
  { key: 'manager',          type: 'custom',   label: 'Manager',           section: 'request', col_span: 2 },
  { key: 'roles_requested',  type: 'custom',   label: 'Roles Requested',   section: 'request', col_span: 2 },
  { key: 'business_reason',  type: 'textarea', label: 'Business Reason',   section: 'request', col_span: 2 },
];
const PROVISION_CORE_LAYOUT = toUldCoreLayout(PROVISION_FIELD_DEFS);

/**
 * Turns a routine's error_code into a localized sentence.
 *
 * The codes are dotted (CSM.PROVISION.BAD_EMAIL) but i18next splits keys on '.',
 * so a dotted key would send it hunting for nested objects and always miss.
 * Underscoring keeps one flat key per code. An unmapped code falls back to the
 * generic message rather than showing the raw code to the user.
 */
function provisionError(
  t: TFunction,
  code: string | undefined,
  fallback: string,
): string {
  if (!code) return fallback;
  return t(`user_provision.error.${code.replace(/\./g, '_')}`, fallback);
}

export interface UserProvisionPageProps {
  session: AuthSession;
}

export function UserProvisionPage({ session }: UserProvisionPageProps) {
  return (
    <PersonalizationProvider pageCode="CSM.USER.PROVISION" session={session}>
      <UserProvisionPageInner session={session} />
    </PersonalizationProvider>
  );
}

function UserProvisionPageInner({ session }: UserProvisionPageProps) {
  const { t } = useTranslation();
  // §8: resolve _tl data against the ACTIVE locale (follows a runtime switch), not
  // the stored preferred_locale (stale in memory until re-login).
  const activeLocale = useActiveLocale();
  const { isPersonalizing, stopPersonalizing } = usePersonalization();
  const meId = session.effective_user.user_id;
  const [tab, setTab]       = useState<Tab>('pending');

  // Personalization is scoped to the active tab — switching tabs exits the mode
  // so it never "bleeds" from one tab's screen onto another's.
  useEffect(() => { stopPersonalizing(); }, [tab, stopPersonalizing]);
  const [filter, setFilter] = useState(() => sessionStorage.getItem(FILTER_KEY) ?? '');
  // §17.B5: per-column multi-value facets (logical columns of the request list).
  const [facets, setFacets] = useState<{ status: string[]; job: string[] }>({ status: [], job: [] });

  const [jobs, setJobs]     = useState<JobOpt[]>([]);
  const [roles, setRoles]   = useState<RoleOpt[]>([]);
  const [rows, setRows]     = useState<RequestRow[]>([]);
  const [opening, setOpening] = useState<RequestRow | null>(null);

  const [busy, setBusy]   = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sort = useSort<RequestSortKey>('csm.provision.sort', { key: 'request_internal_code', dir: 'desc' });

  useEffect(() => { sessionStorage.setItem(FILTER_KEY, filter); }, [filter]);

  // Reference data.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const locale = activeLocale;
      // Approver count is now resolved through the linked csm_approval_hierarchy
      // rather than the deprecated csm_job_description_approver table.
      const { data: jData } = await apiDb
        .from('csm_job_description')
        .select(`
          job_description_id, internal_code, is_active, approval_hierarchy_id,
          csm_job_description_tl ( locale_code, display_text ),
          csm_approval_hierarchy:approval_hierarchy_id (
            csm_approval_hierarchy_step ( approval_hierarchy_step_id, is_active )
          )
        `)
        .eq('is_active', 'Y')
        .order('internal_code');
      if (cancelled) return;
      type JobDataRow = {
        job_description_id: number;
        internal_code: string;
        csm_job_description_tl?: TlRow[];
        csm_approval_hierarchy?: { csm_approval_hierarchy_step?: { approval_hierarchy_step_id: number; is_active: string }[] } | null;
      };
      const jOpts: JobOpt[] = (jData ?? []).map((j: JobDataRow) => {
        const tls = j.csm_job_description_tl ?? [];
        const exact = tls.find((t: TlRow) => t.locale_code === locale);
        const fb    = tls.find((t: TlRow) => t.locale_code === 'EN_US') ?? tls[0];
        const steps = j.csm_approval_hierarchy?.csm_approval_hierarchy_step ?? [];
        const approverCount = steps.filter((s) => s.is_active === 'Y').length;
        return {
          job_description_id: j.job_description_id,
          internal_code: j.internal_code,
          display_text: exact?.display_text ?? fb?.display_text ?? j.internal_code,
          approver_count: approverCount,
        };
      });
      setJobs(jOpts);

      const { data: rData } = await apiDb
        .from('csm_role_master')
        .select(`role_id, internal_code, is_active,
                 csm_role_master_tl ( locale_code, display_text )`)
        .eq('is_active', 'Y')
        .order('internal_code');
      if (cancelled) return;
      type RoleDataRow = { role_id: number; internal_code: string; csm_role_master_tl?: TlRow[] };
      const rOpts: RoleOpt[] = (rData ?? []).map((r: RoleDataRow) => {
        const tls = r.csm_role_master_tl ?? [];
        const exact = tls.find((t: TlRow) => t.locale_code === locale);
        const fb    = tls.find((t: TlRow) => t.locale_code === 'EN_US') ?? tls[0];
        return {
          role_id: r.role_id,
          internal_code: r.internal_code,
          display_text: exact?.display_text ?? fb?.display_text ?? r.internal_code,
        };
      });
      setRoles(rOpts);
    })();
    return () => { cancelled = true; };
  }, [activeLocale]);

  const reload = async () => {
    setBusy(true); setError(null);
    try {
      // Provisioning requests ARE apr_requests now (20260827010000). Two reads
      // rather than one embedded query: the pending-approver label needs
      // apr_step, and naming the FK constraints for a PostgREST embed would
      // couple this screen to constraint names the engine is free to rename.
      const { data: reqData, error: e } = await apiDb
        .from('apr_request')
        .select(`
          approval_request_id, state, current_level, active_levels,
          staged_payload, requested_by_user_id, requested_at, decided_at,
          config:apr_config!approval_config_id ( internal_code ),
          requester:csm_user_master!requested_by_user_id ( display_name, email )
        `)
        .eq('entity_name', 'csm_user_master')
        .order('approval_request_id', { ascending: false });
      if (e) throw e;

      type AprReqRow = {
        approval_request_id: number;
        state: string;
        current_level: number;
        active_levels: number[] | null;
        staged_payload: { row?: Record<string, unknown> } | null;
        requested_by_user_id: number;
        requested_at: string | null;
        decided_at: string | null;
        config?: { internal_code: string } | null;
        requester?: { display_name: string; email: string } | null;
      };
      // Other entities can have approvals on csm_user_master; only this config's
      // requests are provisioning.
      const provisionReqs = ((reqData ?? []) as AprReqRow[])
        .filter(r => r.config?.internal_code === 'CSM.USER_PROVISIONING');
      const reqIds = provisionReqs.map(r => r.approval_request_id);

      // Who is being waited on: the undecided step(s) at an active level.
      const approverByRequest = new Map<number, { id: number; label: string }>();
      if (reqIds.length > 0) {
        const { data: stepData } = await apiDb
          .from('apr_step')
          .select(`approval_request_id, level, approver_user_id, decision,
                   approver:csm_user_master!approver_user_id ( display_name, email )`)
          .in('approval_request_id', reqIds)
          .is('decision', null);
        type StepRow = {
          approval_request_id: number; level: number; approver_user_id: number;
          approver?: { display_name: string; email: string } | null;
        };
        for (const st of ((stepData ?? []) as StepRow[])) {
          const req = provisionReqs.find(r => r.approval_request_id === st.approval_request_id);
          const active = req?.active_levels ?? (req ? [req.current_level] : []);
          if (!active.includes(st.level)) continue;
          if (!approverByRequest.has(st.approval_request_id)) {
            approverByRequest.set(st.approval_request_id, {
              id: st.approver_user_id,
              label: st.approver?.display_name ?? st.approver?.email ?? '—',
            });
          }
        }
      }

      // Job label for each request's staged job_description_id (§17.B4 — the
      // grid shows the resolved name, never the id sitting in the payload).
      const jobLabelById = new Map<number, string>(jobs.map(j => [j.job_description_id, j.display_text]));

      const list: RequestRow[] = provisionReqs.map((r) => {
        const row = (r.staged_payload?.row ?? {}) as Record<string, unknown>;
        const jobId = Number(row.job_description_id ?? 0);
        const waiting = approverByRequest.get(r.approval_request_id) ?? null;
        return {
          approval_request_id: r.approval_request_id,
          // The engine has no per-request business code; a stable, readable
          // reference derived from the id keeps the column meaningful.
          request_internal_code: `PRV-${String(r.approval_request_id).padStart(5, '0')}`,
          target_email: String(row.email ?? ''),
          target_display_name: String(row.display_name ?? ''),
          target_job_label: jobLabelById.get(jobId) ?? '',
          requested_by: r.requested_by_user_id,
          requester_label: r.requester?.display_name ?? r.requester?.email ?? '—',
          status_code: r.state,
          status_label: t(`user_provision.state.${r.state}`, r.state),
          current_sequence_order: r.current_level,
          current_approver_id: waiting?.id ?? null,
          current_approver_label: waiting?.label ?? null,
          business_reason: null,
          submitted_at: r.requested_at,
          finalized_at: r.decided_at,
        };
      });
      setRows(list);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // reload is re-created each render; reload only when the active tab changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void reload();   }, [tab, activeLocale]);

  // Tab-scoped rows (before text/facet filtering) — also the basis for facet options.
  const scopedRows = useMemo(() => {
    if (tab === 'pending') return rows.filter(r => r.current_approver_id === meId && r.status_code === 'PENDING');
    if (tab === 'mine')    return rows.filter(r => r.requested_by === meId);
    return [];
  }, [rows, tab, meId]);

  // §17.B5: distinct facet options derived from the tab-scoped rows.
  const statusFacetOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const r of scopedRows) if (!seen.has(r.status_code)) seen.set(r.status_code, r.status_label || r.status_code);
    return [...seen].map(([value, label]) => ({ value, label }));
  }, [scopedRows]);
  const jobFacetOptions = useMemo(() => {
    const uniq = [...new Set(scopedRows.map(r => r.target_job_label).filter(Boolean))].sort();
    return uniq.map(v => ({ value: v, label: v }));
  }, [scopedRows]);
  const anyFilterActive = filter.trim().length > 0 || facets.status.length + facets.job.length > 0;
  const clearAll = () => { setFilter(''); setFacets({ status: [], job: [] }); };

  // §17.B13: related-children expand/contract (keyed by user_provision_request_id). The
  // list rows carry resolved parent references (job, awaiting approver, requester) —
  // surface those as related context. Raw ids never appear (§17.B4).
  const detail = useExpandedRows<number>();
  const buildRelatedGroups = (id: number): RelatedChildGroup[] => {
    const row = rows.find(r => r.approval_request_id === id);
    if (!row) return [];
    return [
      {
        key: 'job',
        table: 'csm_job_description', pkColumn: 'job_description_id',
        title: t('user_provision.list.columns.job', 'Job'),
        items: row.target_job_label ? [{ id: `job-${id}`, label: row.target_job_label }] : [],
      },
      {
        key: 'awaiting',
        title: t('user_provision.list.columns.awaiting', 'Awaiting'),
        items: row.current_approver_label ? [{ id: `apr-${id}`, label: row.current_approver_label }] : [],
      },
      {
        key: 'requester',
        table: 'csm_user_master', pkColumn: 'user_id',
        title: t('user_provision.detail.summary.requested_by', 'Requested by'),
        items: row.requester_label && row.requester_label !== '—'
          ? [{ id: row.requested_by, label: row.requester_label }]
          : [],
      },
    ];
  };
  const hasRelated = (id: number) => buildRelatedGroups(id).some(g => g.items.length > 0);

  const filtered = useMemo(() => {
    const q = filter.toLowerCase();
    // A row passes a facet when no values are selected, or it matches one (OR within
    // a facet, AND across facets).
    const fil = scopedRows.filter(r => {
      const matchText = !filter.trim() ||
        r.request_internal_code.toLowerCase().includes(q) ||
        r.target_email.toLowerCase().includes(q) ||
        r.target_display_name.toLowerCase().includes(q) ||
        r.target_job_label.toLowerCase().includes(q);
      const matchStatus = facets.status.length === 0 || facets.status.includes(r.status_code);
      const matchJob    = facets.job.length === 0    || facets.job.includes(r.target_job_label);
      return matchText && matchStatus && matchJob;
    });
    return sort.sorted(fil, (a, b, key) => {
      if (key === 'current_sequence_order') {
        return (a.current_sequence_order ?? 0) - (b.current_sequence_order ?? 0);
      }
      return compareStrings((a as unknown as Record<string, unknown>)[key], (b as unknown as Record<string, unknown>)[key]);
    });
  }, [scopedRows, filter, facets, sort]);

  return (
    <div className="flex h-full flex-col">
      {/* Tabs */}
      <div className="flex h-9 shrink-0 items-end border-b border-csm-border bg-white px-3">
        {([
          ['submit',  t('user_provision.tabs.submit')],
          ['pending', t('user_provision.tabs.pending')],
          ['mine',    t('user_provision.tabs.mine')],
        ] as [Tab, string][]).map(([code, label]) => (
          <button
            key={code} type="button"
            onClick={() => setTab(code)}
            className={
              'mr-1 h-9 border-b-2 px-3 text-csm-sm uppercase tracking-wider ' +
              (tab === code
                ? 'border-sidebar text-csm-ink'
                : 'border-transparent text-csm-muted hover:text-csm-ink-soft')
            }
          >{label}</button>
        ))}
      </div>

      {tab === 'submit' ? (
        <SubmitNewUserForm
          session={session}
          jobs={jobs} roles={roles}
          onSubmitted={() => { setTab('mine'); void reload(); }}
        />
      ) : (
        <>
          <div className="flex h-10 shrink-0 items-center gap-2 border-b border-csm-border bg-white px-3">
            <input
              type="text" value={filter} onChange={e => setFilter(e.target.value)}
              placeholder={t('user_provision.list.search_placeholder')}
              className="h-7 w-80 rounded border border-csm-border px-2 text-csm-md focus:border-csm-watermark focus:outline-none"
            />
            <FacetFilter label={t('user_provision.list.columns.status', 'Status')} options={statusFacetOptions} selected={facets.status} onChange={v => setFacets(f => ({ ...f, status: v }))} />
            <FacetFilter label={t('user_provision.list.columns.job', 'Job')} options={jobFacetOptions} selected={facets.job} onChange={v => setFacets(f => ({ ...f, job: v }))} />
            <ClearFiltersButton active={anyFilterActive} onClear={clearAll} label={t('common.clear_filters', 'Clear filters')} />
            <button onClick={() => void reload()} className="h-7 rounded border border-csm-border bg-white px-2 text-csm-sm hover:bg-csm-surface-alt">{t('common.refresh')}</button>
            {busy && <span className="text-csm-xs text-csm-watermark">{t('common.loading')}</span>}
            <div className="ml-auto"><PersonalizationToggle /></div>
          </div>
          {error && (
            <div className="shrink-0 px-3 pt-2">
              <ErrorBanner
                message={t('user_provision.list.load_failed', "Couldn't load provisioning requests.")}
                detail={error}
                retry_label={t('common.retry', 'Retry')}
                on_retry={() => void reload()}
                details_label={t('common.details', 'Details')}
              />
            </div>
          )}

          {isPersonalizing && (
            <div className="shrink-0 border-b border-primary/20 bg-primary/5 px-3 py-1.5 text-csm-xs text-primary">
              {t('user_provision.personalize_hint', 'Personalization mode — configure columns below, then toggle off to save.')}
            </div>
          )}

          <div className="flex-1 overflow-auto">
            {busy && rows.length === 0 ? (
              <SkeletonRows rows={8} />
            ) : (
            <PersonalizedGrid componentKey="grid" columns={GRID_COLS}>
              {(visibleCols, widths) => (
                <table className={GRID_TABLE_CLASS}>
                  <thead className="sticky top-0 z-10 bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
                    <tr>
                      <th className="w-7 border-b border-csm-border px-1 py-1.5" title={t('common.related_lbl', 'Related records')} />
                      {visibleCols.map(col => {
                        if (col.key === 'request_internal_code')  return <SortableHeader<RequestSortKey> key={col.key} sortKey="request_internal_code"  label={t('user_provision.list.columns.request')}       currentKey={sort.sortKey} currentDir={sort.sortDir} onSort={sort.setSort} style={{ width: widths[col.key] }} />;
                        if (col.key === 'target_email')           return <SortableHeader<RequestSortKey> key={col.key} sortKey="target_email"           label={t('user_provision.list.columns.target_email')}  currentKey={sort.sortKey} currentDir={sort.sortDir} onSort={sort.setSort} style={{ width: widths[col.key] }} />;
                        if (col.key === 'target_display_name')    return <SortableHeader<RequestSortKey> key={col.key} sortKey="target_display_name"    label={t('user_provision.list.columns.display_name')}  currentKey={sort.sortKey} currentDir={sort.sortDir} onSort={sort.setSort} style={{ width: widths[col.key] }} />;
                        if (col.key === 'target_job_label')       return <SortableHeader<RequestSortKey> key={col.key} sortKey="target_job_label"       label={t('user_provision.list.columns.job')}           currentKey={sort.sortKey} currentDir={sort.sortDir} onSort={sort.setSort} style={{ width: widths[col.key] }} />;
                        if (col.key === 'status_code')            return <SortableHeader<RequestSortKey> key={col.key} sortKey="status_code"            label={t('user_provision.list.columns.status')}        currentKey={sort.sortKey} currentDir={sort.sortDir} onSort={sort.setSort} style={{ width: widths[col.key] }} />;
                        if (col.key === 'current_sequence_order') return <SortableHeader<RequestSortKey> key={col.key} sortKey="current_sequence_order" label={t('user_provision.list.columns.step')}          currentKey={sort.sortKey} currentDir={sort.sortDir} onSort={sort.setSort} style={{ width: widths[col.key] }} />;
                        if (col.key === 'current_approver_label') return <SortableHeader<RequestSortKey> key={col.key} sortKey="current_approver_label" label={t('user_provision.list.columns.awaiting')}      currentKey={sort.sortKey} currentDir={sort.sortDir} onSort={sort.setSort} style={{ width: widths[col.key] }} />;
                        return null;
                      })}
                      <th className="border-b border-csm-border px-3 py-1.5 text-left" style={{ width: '1%' }}>{t('user_provision.list.columns.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(r => (
                      <Fragment key={r.approval_request_id}>
                      <tr
                          className="hover:bg-csm-surface-alt">
                        <td className="border-b border-csm-border-soft px-1 py-1.5 text-center align-middle" onClick={e => e.stopPropagation()}>
                          <RelatedExpandButton
                            open={detail.isOpen(r.approval_request_id)}
                            hasChildren={hasRelated(r.approval_request_id)}
                            onToggle={() => detail.toggle(r.approval_request_id)}
                            expandLabel={t('common.related_expand', 'Show related records')}
                            collapseLabel={t('common.related_collapse', 'Hide related')}
                            tooltip={t('common.related_lbl', 'Related records')}
                          />
                        </td>
                        {visibleCols.map(col => {
                          if (col.key === 'request_internal_code')  return <td key={col.key} style={{ width: widths[col.key] }} className="border-b border-csm-border-soft px-3 py-1.5 font-mono text-csm-sm">{r.request_internal_code}</td>;
                          if (col.key === 'target_email')           return <td key={col.key} style={{ width: widths[col.key] }} className="border-b border-csm-border-soft px-3 py-1.5">{r.target_email}</td>;
                          if (col.key === 'target_display_name')    return <td key={col.key} style={{ width: widths[col.key] }} className="border-b border-csm-border-soft px-3 py-1.5">{r.target_display_name}</td>;
                          if (col.key === 'target_job_label')       return <td key={col.key} style={{ width: widths[col.key] }} className="border-b border-csm-border-soft px-3 py-1.5">{r.target_job_label}</td>;
                          if (col.key === 'status_code')            return <td key={col.key} style={{ width: widths[col.key] }} className="border-b border-csm-border-soft px-3 py-1.5"><ProvisionStatusBadge code={r.status_code} label={r.status_label} /></td>;
                          if (col.key === 'current_sequence_order') return (
                            <td key={col.key} style={{ width: widths[col.key] }} className="border-b border-csm-border-soft px-3 py-1.5">
                              {r.current_sequence_order === 0
                                ? <span className="rounded border border-csm-info-border bg-csm-info-soft px-1.5 py-0.5 text-csm-2xs uppercase tracking-wider text-csm-info">{t('user_provision.list.manager_badge')}</span>
                                : r.current_sequence_order}
                            </td>
                          );
                          if (col.key === 'current_approver_label') return (
                            <td key={col.key} style={{ width: widths[col.key] }} className="border-b border-csm-border-soft px-3 py-1.5 text-csm-ink-soft">
                              {r.current_approver_label ?? <span className="italic text-csm-watermark">{t('user_provision.list.awaiting_finalized')}</span>}
                            </td>
                          );
                          return null;
                        })}
                        <td className="border-b border-csm-border-soft px-3 py-1.5 text-right">
                          <button onClick={(e) => { e.stopPropagation(); setOpening(r); }}
                                  className="rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-border-soft">{t('user_provision.list.actions.open')}</button>
                        </td>
                      </tr>
                      {detail.isOpen(r.approval_request_id) && (
                        <tr>
                          <td colSpan={visibleCols.length + 2} className="border-b border-csm-border bg-csm-surface-alt/40 px-3 py-3" onClick={e => e.stopPropagation()}>
                            <RelatedChildrenPanel groups={buildRelatedGroups(r.approval_request_id)} session={session} />
                          </td>
                        </tr>
                      )}
                      </Fragment>
                    ))}
                    {filtered.length === 0 && !busy && (
                      <tr><td colSpan={visibleCols.length + 2} className="px-3 py-8 text-center">
                        <EmptyState
                          variant={anyFilterActive ? 'no-match' : tab === 'pending' ? 'all-clear' : 'no-data'}
                          title={tab === 'pending' ? t('user_provision.list.empty_pending') : t('user_provision.list.empty_mine')}
                        />
                      </td></tr>
                    )}
                  </tbody>
                </table>
              )}
            </PersonalizedGrid>
            )}
          </div>
        </>
      )}

      {opening && (
        <RequestDetailModal
          session={session}
          request={opening}
          onClose={() => setOpening(null)}
          onChanged={() => { setOpening(null); void reload(); }}
        />
      )}
    </div>
  );
}

// =============================================================================
// SUBMIT FORM
// =============================================================================

interface SubmitNewUserFormProps {
  session: AuthSession;
  jobs: JobOpt[];
  roles: RoleOpt[];
  onSubmitted: () => void;
}

function SubmitNewUserForm({ session, jobs, roles, onSubmitted }: SubmitNewUserFormProps) {
  const { t } = useTranslation();
  const [email, setEmail]               = useState('');
  const [displayName, setDisplayName]   = useState('');
  const [locale, setLocale]             = useState(session.effective_user.preferred_locale);
  const [jobId, setJobId]               = useState<number | ''>('');
  const [managerId, setManagerId]       = useState<number | null>(null);
  const [companyId, setCompanyId]             = useState<number | null>(null);
  const [divisionId, setDivisionId]           = useState<number | null>(null);
  const [businessUnitId, setBusinessUnitId]   = useState<number | null>(null);
  const [officeId, setOfficeId]               = useState<number | null>(null);
  const [reason, setReason]             = useState('');
  const [selectedRoles, setSelectedRoles] = useState<Set<number>>(new Set());
  const [jobDefaultRoles, setJobDefaultRoles] = useState<Map<number, number[]>>(new Map());
  const [prevJobId, setPrevJobId] = useState<number | ''>('');
  const [reconcile, setReconcile] = useState<{ oldDefaults: number[]; newDefaults: number[] } | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const map = await fetchAllJobDefaultRoles();
      if (!cancelled) setJobDefaultRoles(map);
    })();
    return () => { cancelled = true; };
  }, []);
  const [users, setUsers]               = useState<{ user_id: number; display_name: string; email: string }[]>([]);
  const [busy, setBusy]   = useState(false);
  const [error, setError] = useState<string | null>(null);

  const orgLookups = useOrgLookups(session.effective_user.preferred_locale);

  // §8: localize section header labels by key. FieldRenderer takes section labels
  // from the (ULD) sectionLayout when present, and applySectionedLayout copies these
  // base labels through — so they must be localized HERE, before the hook.
  const localizedSections = PROVISION_SECTIONS.map(s => ({ ...s, label: t(`user_provision.section.${s.key}`, { defaultValue: s.label }) }));
  const { sections: provisionLayout } = useUldSectionedLayout('CSM.USER.PROVISION', PROVISION_CORE_LAYOUT, localizedSections);

  // Pre-select org defaults exactly once (when lookups have finished loading).
  // The user can still change any of them before submitting.
  const [defaultsApplied, setDefaultsApplied] = useState(false);
  useEffect(() => {
    if (orgLookups.loading || defaultsApplied) return;
    setCompanyId      (prev => prev ?? orgLookups.defaults.company_id);
    setDivisionId     (prev => prev ?? orgLookups.defaults.division_id);
    setBusinessUnitId (prev => prev ?? orgLookups.defaults.business_unit_id);
    setOfficeId       (prev => prev ?? orgLookups.defaults.office_id);
    setDefaultsApplied(true);
  }, [orgLookups.loading, orgLookups.defaults, defaultsApplied]);

  // Load active users for the manager picker.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const { data } = await apiDb
        .from('csm_user_master')
        .select('user_id, display_name, email')
        .eq('is_active', 'Y')
        .order('display_name');
      if (!cancelled) {
        setUsers((data ?? []) as { user_id: number; display_name: string; email: string }[]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const job = jobs.find(j => j.job_description_id === jobId);
  const roleLabels = useMemo(() => new Map(roles.map(r => [r.role_id, r.display_text])), [roles]);

  const handleJobChange = (newJobId: number | '') => {
    setJobId(newJobId);
    if (newJobId === '') { setPrevJobId(''); return; }
    // Only reconcile roles that resolve to an active label — a job's default link
    // may point at a role since deactivated; showing its bare id would leak a raw
    // surrogate id (§17.B4), and an inactive role can't be assigned anyway.
    const active = (ids: number[]) => ids.filter(id => roleLabels.has(id));
    const N = active(jobDefaultRoles.get(newJobId) ?? []);
    const O = prevJobId === '' ? [] : active(jobDefaultRoles.get(prevJobId) ?? []);
    const S = [...selectedRoles];
    // NOTE: prevJobId tracks only the immediately-previous job. Passing THROUGH a
    // job with no defaults resets O to [] on the next change, so 'swap'/'remove_old'
    // can no longer strip roles seeded by a job two steps back. Acceptable: the user
    // can still Replace/Customize. Single-prevJobId limitation by design.
    if (N.length === 0) { setPrevJobId(newJobId); return; }
    if (S.length === 0) {
      setSelectedRoles(new Set(applyRoleStrategy('replace', S, O, N)));
      setPrevJobId(newJobId);
      return;
    }
    setReconcile({ oldDefaults: O, newDefaults: N });
  };

  const toggleRole = (roleId: number) => {
    setSelectedRoles(prev => {
      const next = new Set(prev);
      if (next.has(roleId)) next.delete(roleId); else next.add(roleId);
      return next;
    });
  };

  const submit = async () => {
    setBusy(true); setError(null);
    try {
      if (!email || !email.includes('@')) throw new Error('A valid email is required.');
      if (!displayName.trim()) throw new Error('Display name is required.');
      if (!jobId) throw new Error('Pick a job description.');
      if (selectedRoles.size === 0) throw new Error('Select at least one role.');
      if (!reason.trim() || reason.trim().length < 5) throw new Error('Provide a business reason of at least 5 characters.');
      if (job && job.approver_count < 1) {
        throw new Error('The chosen job description has no approver chain. Configure approvers in Job Descriptions first.');
      }

      // Opens an APR request with one line per requested role. Roles are passed
      // as internal_codes, not ids: apr_request_line keys on a stable business
      // code so a decision can be posted without reading ids back first.
      const roleCodes = roles.filter(r => selectedRoles.has(r.role_id)).map(r => r.internal_code);
      const { data: res, error: e } = await apiDb.rpc<{ ok: boolean; error_code?: string }>(
        'csm_provision_submit', {
          p_email:              email.trim(),
          p_display_name:       displayName.trim(),
          p_job_description_id: jobId,
          p_role_codes:         roleCodes,
          p_preferred_locale:   locale,
          p_manager_user_id:    managerId,
          p_business_reason:    reason.trim(),
          p_company_id:         companyId,
          p_division_id:        divisionId,
          p_business_unit_id:   businessUnitId,
          p_office_id:          officeId,
        });
      if (e) throw e;
      // The routine reports refusals in its envelope rather than raising, so an
      // unchecked call would look like success and silently open nothing.
      if (!res?.ok) throw new Error(provisionError(t, res?.error_code, 'Submit failed.'));

      onSubmitted();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex-1 overflow-auto">
      <div className="mx-auto max-w-3xl p-6">
        <div className="mb-2 flex items-center justify-between">
          <h2 className="text-csm-base font-semibold text-csm-ink">{t('user_provision.submit.heading', 'Request a new user')}</h2>
          <PersonalizationToggle />
        </div>
        <p className="mb-4 text-csm-sm text-csm-muted">
          {t('user_provision.submit.intro', "Approval is sequential, defined by the chosen job description's approver chain. Each approver decides per role; rejected roles drop, surviving roles continue.")}
        </p>

        <div className="mb-3">
          <FieldRenderer
            fields={PROVISION_FIELD_DEFS}
            sections={localizedSections}
            sectionLayout={provisionLayout}
            columns={2}
            pageCode="CSM.USER.PROVISION"
            session={session}
            render={(f) => {
              if (f.key === 'target_email') return (
                <PersonalizedField componentKey="form" fieldKey="target_email" label={t('user_provision.submit.field.target_email', 'Target Email')}>
                  <div>
                    <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('user_provision.submit.field.target_email', 'Target Email')}</label>
                    <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                           className="h-8 w-full rounded border border-csm-border px-2 text-csm-md focus:border-csm-watermark focus:outline-none" />
                  </div>
                </PersonalizedField>
              );
              if (f.key === 'display_name') return (
                <PersonalizedField componentKey="form" fieldKey="display_name" label={t('user_provision.submit.field.display_name', 'Display Name')}>
                  <div>
                    <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('user_provision.submit.field.display_name', 'Display Name')}</label>
                    <input type="text" value={displayName} onChange={e => setDisplayName(e.target.value)}
                           className="h-8 w-full rounded border border-csm-border px-2 text-csm-md focus:border-csm-watermark focus:outline-none" />
                  </div>
                </PersonalizedField>
              );
              if (f.key === 'preferred_locale') return (
                <PersonalizedField componentKey="form" fieldKey="preferred_locale" label={t('user_provision.submit.field.preferred_locale', 'Preferred Locale')}>
                  <div>
                    <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('user_provision.submit.field.preferred_locale', 'Preferred Locale')}</label>
                    <LocaleSelect value={locale} onChange={setLocale} />
                  </div>
                </PersonalizedField>
              );
              if (f.key === 'job_description') return (
                <PersonalizedField componentKey="form" fieldKey="job_description" label={t('user_provision.submit.field.job_description', 'Job Description')}>
                  <div>
                    <FilteredModalSingleSelect
                      label={t('user_provision.submit.field.job_description', 'Job Description')}
                      options={jobs.map(j => ({
                        internal_code: j.internal_code,
                        display_text: `${j.display_text}`,
                        description: `${j.internal_code} - ${j.approver_count} approver${j.approver_count === 1 ? '' : 's'}`,
                      }))}
                      selectedCode={
                        jobId === '' ? null : jobs.find(j => j.job_description_id === jobId)?.internal_code ?? null
                      }
                      onChange={(code) => {
                        if (code === null) handleJobChange('');
                        else {
                          const j = jobs.find(jj => jj.internal_code === code);
                          handleJobChange(j?.job_description_id ?? '');
                        }
                      }}
                      placeholder={t('user_provision.submit.field.job_description_placeholder', 'Select a job description...')}
                      allowClear
                    />
                    {job && job.approver_count === 0 && (
                      <div className="mt-1 text-csm-2xs text-csm-danger">
                        {t('user_provision.submit.field.job_no_approver', 'This job has no approver chain configured.')}
                      </div>
                    )}
                  </div>
                </PersonalizedField>
              );
              if (f.key === 'manager') return (
                <PersonalizedField componentKey="form" fieldKey="manager" label={t('user_provision.submit.field.manager', 'Manager (optional)')}>
                  <div>
                    <FilteredModalSingleSelect
                      label={t('user_provision.submit.field.manager', 'Manager (optional)')}
                      options={users.map(u => ({
                        internal_code: String(u.user_id),
                        display_text: u.display_name || u.email,
                        description: u.display_name ? u.email : undefined,
                      }))}
                      selectedCode={managerId == null ? null : String(managerId)}
                      onChange={(code) => setManagerId(code === null ? null : Number(code))}
                      placeholder={t('user_provision.submit.field.manager_placeholder', '(none)')}
                      allowClear
                      clearLabel={t('user_provision.submit.field.manager_clear_label', '(no manager)')}
                    />
                    <div className="mt-1 text-csm-2xs text-csm-muted">
                      {t('user_provision.submit.field.manager_hint', "If the chosen job's hierarchy requires manager approval, this person becomes the first approver. If you are the manager, your approval is auto-recorded.")}
                    </div>
                  </div>
                </PersonalizedField>
              );
              if (f.key === 'company') return (
                <PersonalizedField componentKey="form" fieldKey="company" label={t('user_provision.submit.field.company', 'Company')}>
                  <FilteredModalSingleSelect
                    label={t('user_provision.submit.field.company', 'Company')}
                    options={orgLookups.companies.map(c => ({
                      internal_code: c.internal_code,
                      display_text:  c.display_text + (c.is_default === 'Y' ? t('user_provision.submit.field.default_suffix', '  (default)') : ''),
                    }))}
                    selectedCode={companyId == null ? null : (orgLookups.companies.find(c => c.id === companyId)?.internal_code ?? null)}
                    onChange={(code) => setCompanyId(code == null ? null : (orgLookups.companies.find(c => c.internal_code === code)?.id ?? null))}
                    placeholder={t('user_provision.submit.field.org_placeholder', '(none)')} allowClear clearLabel={t('user_provision.submit.field.org_clear_label', '(none)')}
                  />
                </PersonalizedField>
              );
              if (f.key === 'division') return (
                <PersonalizedField componentKey="form" fieldKey="division" label={t('user_provision.submit.field.division', 'Division')}>
                  <FilteredModalSingleSelect
                    label={t('user_provision.submit.field.division', 'Division')}
                    options={orgLookups.divisions.map(d => ({
                      internal_code: d.internal_code,
                      display_text:  d.display_text + (d.is_default === 'Y' ? t('user_provision.submit.field.default_suffix', '  (default)') : ''),
                    }))}
                    selectedCode={divisionId == null ? null : (orgLookups.divisions.find(d => d.id === divisionId)?.internal_code ?? null)}
                    onChange={(code) => setDivisionId(code == null ? null : (orgLookups.divisions.find(d => d.internal_code === code)?.id ?? null))}
                    placeholder={t('user_provision.submit.field.org_placeholder', '(none)')} allowClear clearLabel={t('user_provision.submit.field.org_clear_label', '(none)')}
                  />
                </PersonalizedField>
              );
              if (f.key === 'business_unit') return (
                <PersonalizedField componentKey="form" fieldKey="business_unit" label={t('user_provision.submit.field.business_unit', 'Business Unit')}>
                  <FilteredModalSingleSelect
                    label={t('user_provision.submit.field.business_unit', 'Business Unit')}
                    options={orgLookups.business_units.map(b => ({
                      internal_code: b.internal_code,
                      display_text:  b.display_text + (b.is_default === 'Y' ? t('user_provision.submit.field.default_suffix', '  (default)') : ''),
                    }))}
                    selectedCode={businessUnitId == null ? null : (orgLookups.business_units.find(b => b.id === businessUnitId)?.internal_code ?? null)}
                    onChange={(code) => setBusinessUnitId(code == null ? null : (orgLookups.business_units.find(b => b.internal_code === code)?.id ?? null))}
                    placeholder={t('user_provision.submit.field.org_placeholder', '(none)')} allowClear clearLabel={t('user_provision.submit.field.org_clear_label', '(none)')}
                  />
                </PersonalizedField>
              );
              if (f.key === 'office') return (
                <PersonalizedField componentKey="form" fieldKey="office" label={t('user_provision.submit.field.office', 'Office')}>
                  <FilteredModalSingleSelect
                    label={t('user_provision.submit.field.office', 'Office')}
                    options={orgLookups.offices.map(o => ({
                      internal_code: o.internal_code,
                      display_text:  o.display_text + (o.is_default === 'Y' ? t('user_provision.submit.field.default_suffix', '  (default)') : ''),
                    }))}
                    selectedCode={officeId == null ? null : (orgLookups.offices.find(o => o.id === officeId)?.internal_code ?? null)}
                    onChange={(code) => setOfficeId(code == null ? null : (orgLookups.offices.find(o => o.internal_code === code)?.id ?? null))}
                    placeholder={t('user_provision.submit.field.org_placeholder', '(none)')} allowClear clearLabel={t('user_provision.submit.field.org_clear_label', '(none)')}
                  />
                </PersonalizedField>
              );
              if (f.key === 'roles_requested') return (
                <PersonalizedField componentKey="form" fieldKey="roles_requested" label={t('user_provision.submit.field.roles_requested', 'Roles Requested')}>
                  <div>
                    <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('user_provision.submit.field.roles_requested', 'Roles Requested')}</label>
                    <div className="overflow-hidden rounded border border-csm-border">
                      <table className="w-full text-csm-sm">
                        <thead className="bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
                          <tr>
                            <th className="w-6 px-1 py-1"></th>
                            <th className="px-2 py-1 text-left">{t('user_provision.submit.roles.col_role', 'Role')}</th>
                            <th className="px-2 py-1 text-left">{t('user_provision.submit.roles.col_code', 'Code')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {roles.map(r => (
                            <tr key={r.role_id} className="border-t border-csm-border-soft hover:bg-csm-surface-alt cursor-pointer"
                                onClick={() => toggleRole(r.role_id)}>
                              <td className="px-1 py-1 text-center">
                                <input type="checkbox" readOnly checked={selectedRoles.has(r.role_id)}
                                       className="h-3.5 w-3.5 accent-gray-700" />
                              </td>
                              <td className="px-2 py-1">{r.display_text}</td>
                              <td className="px-2 py-1 font-mono text-csm-2xs text-csm-muted">{r.internal_code}</td>
                            </tr>
                          ))}
                          {roles.length === 0 && (
                            <tr><td colSpan={3} className="px-2 py-3 text-center italic text-csm-watermark">{t('user_provision.submit.roles.empty', 'No active roles.')}</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </PersonalizedField>
              );
              if (f.key === 'business_reason') return (
                <PersonalizedField componentKey="form" fieldKey="business_reason" label={t('user_provision.submit.field.business_reason', 'Business Reason')}>
                  <div>
                    <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('user_provision.submit.field.business_reason', 'Business Reason')}</label>
                    <textarea rows={3} value={reason} onChange={e => setReason(e.target.value)}
                              className="w-full rounded border border-csm-border px-2 py-1 text-csm-md focus:border-csm-watermark focus:outline-none" />
                  </div>
                </PersonalizedField>
              );
              return null;
            }}
          />
        </div>

        {error && (
          <div className="mb-3 rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-1 text-csm-sm text-csm-danger">{error}</div>
        )}

        <div className="flex justify-end gap-2">
          <button onClick={() => void submit()} disabled={busy}
                  className="h-8 rounded border border-sidebar bg-sidebar px-3 text-csm-md text-white hover:bg-sidebar-alt disabled:opacity-60">
            {busy ? t('user_provision.submit.submitting', 'Submitting...') : t('user_provision.submit.submit_button', 'Submit Request')}
          </button>
        </div>
      </div>
        {reconcile && jobId !== '' && (
          <RoleReconciliationModal
            roleLabels={roleLabels}
            current={[...selectedRoles]}
            oldDefaults={reconcile.oldDefaults}
            newDefaults={reconcile.newDefaults}
            onApply={(resultIds) => {
              setSelectedRoles(new Set(resultIds));
              setPrevJobId(jobId);
              setReconcile(null);
            }}
            onCancel={() => {
              setJobId(prevJobId);
              setReconcile(null);
            }}
          />
        )}
    </div>
  );
}

// =============================================================================
// REQUEST DETAIL + APPROVAL DRAWER
// =============================================================================

interface RoleLine {
  /** apr_request_line.line_internal_code — the role code, and the decision key. */
  line_internal_code: string;
  role_label: string;
  status_internal_code: 'PENDING' | 'APPROVED' | 'REJECTED';
  /**
   * The approver's choice for this role, null until they make one. Deliberately
   * NOT defaulted to APPROVE: the engine refuses a decision that leaves any open
   * line unnamed (APR.LINES_INCOMPLETE), because granting a role nobody looked
   * at is the one failure an approval screen must not have. The legacy screen
   * defaulted to APPROVE and did exactly that.
   */
  current_decision: 'APPROVE' | 'REJECT' | null;
}

interface SignoffLine {
  signoff_id: number;
  sequence_order: number;
  signed_by_label: string;
  decision_comment: string | null;
  signed_at: string;
  per_role: { role_label: string; decision: 'APPROVE' | 'REJECT' }[];
}

interface RequestDetailModalProps {
  session: AuthSession;
  request: RequestRow;
  onClose: () => void;
  onChanged: () => void;
}

function RequestDetailModal({ session, request, onClose, onChanged }: RequestDetailModalProps) {
  const { t } = useTranslation();
  const activeLocale = useActiveLocale();
  const meId = getForensicActorId(session);
  const isMyTurn = request.current_approver_id === meId && request.status_code === 'PENDING';

  const [roles, setRoles]       = useState<RoleLine[]>([]);
  const [signoffs, setSignoffs] = useState<SignoffLine[]>([]);
  const [comment, setComment]   = useState('');
  const [busy, setBusy]   = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const { data: rData, error: rErr } = await apiDb
        .from('apr_request_line')
        .select('line_internal_code, line_label, state, sort_order')
        .eq('approval_request_id', request.approval_request_id)
        .order('sort_order');
      if (cancelled) return;
      if (rErr) { setError(rErr.message); return; }
      type LineDataRow = { line_internal_code: string; line_label: string; state: string };
      // line_label was resolved through _tl at submit time, so no join here and
      // no chance of a bare code reaching the approver (§17.B4).
      const rs: RoleLine[] = ((rData ?? []) as LineDataRow[]).map((r) => ({
        line_internal_code: r.line_internal_code,
        role_label: r.line_label,
        status_internal_code: r.state as 'PENDING' | 'APPROVED' | 'REJECTED',
        current_decision: null,
      }));
      setRoles(rs);

      // Decision history: every approver's step, with what they ruled per role.
      const { data: sData } = await apiDb
        .from('apr_step')
        .select(`
          approval_step_id, level, decided_at, decision, decision_comment,
          approver:csm_user_master!approver_user_id ( display_name, email ),
          lines:apr_step_line ( decision,
            line:apr_request_line!approval_request_line_id ( line_label ) )
        `)
        .eq('approval_request_id', request.approval_request_id)
        .order('level');
      if (cancelled) return;
      type StepLineDataRow = { decision: string; line?: { line_label: string } | null };
      type StepDataRow = {
        approval_step_id: number;
        level: number;
        decided_at: string;
        decision_comment: string | null;
        approver?: { display_name: string; email: string } | null;
        lines?: StepLineDataRow[];
      };
      // Only steps someone actually ruled on are history; the apiDb builder has
      // no `.not()`, so the undecided ones are dropped here.
      const so: SignoffLine[] = ((sData ?? []) as StepDataRow[])
        .filter((st) => st.decided_at != null)
        .map((st) => ({
        signoff_id: st.approval_step_id,
        sequence_order: st.level,
        signed_by_label: st.approver?.display_name ?? st.approver?.email ?? '—',
        decision_comment: st.decision_comment,
        signed_at: st.decided_at,
        per_role: (st.lines ?? []).map((l) => ({
          role_label: l.line?.line_label ?? '',
          decision: l.decision as 'APPROVE' | 'REJECT',
        })),
      }));
      setSignoffs(so);
    })();
    return () => { cancelled = true; };
  }, [request.approval_request_id, activeLocale]);  

  const setRoleDecision = (code: string, dec: 'APPROVE' | 'REJECT') => {
    setRoles(prev => prev.map(r =>
      r.line_internal_code === code ? { ...r, current_decision: dec } : r,
    ));
  };

  const submit = async () => {
    setBusy(true); setError(null);
    try {
      const open = roles.filter(r => r.status_internal_code === 'PENDING');
      if (open.length === 0) {
        throw new Error(t('user_provision.detail.err_no_pending', 'No pending roles to act on.'));
      }
      // Every open role needs an explicit verdict. The server enforces this too
      // (APR.LINES_INCOMPLETE); catching it here names the roles still missing
      // instead of showing an error code.
      const undecided = open.filter(r => r.current_decision == null);
      if (undecided.length > 0) {
        throw new Error(t('user_provision.detail.err_undecided',
          'Decide every role before submitting. Still undecided: {{roles}}',
          { roles: undecided.map(r => r.role_label).join(', ') }));
      }

      const { data: res, error: e } = await apiDb.rpc<{ ok: boolean; error_code?: string }>(
        'apr_decide_lines', {
          p_request_id: request.approval_request_id,
          p_lines: open.map(r => ({
            line_internal_code: r.line_internal_code,
            decision: r.current_decision,
          })),
          p_comment: comment || null,
        });
      if (e) throw e;
      if (!res?.ok) throw new Error(provisionError(t, res?.error_code, 'Decision failed.'));
      onChanged();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={request.request_internal_code}
      onClose={onClose}
      width="2xl"
      headerAction={<ProvisionStatusBadge code={request.status_code} label={request.status_label} />}
      footer={
        <>
          <button onClick={onClose} className="rounded border border-csm-border bg-white px-3 py-1 text-csm-sm hover:bg-csm-border-soft">{t('common.close', 'Close')}</button>
          {isMyTurn && (
            <button onClick={() => void submit()} disabled={busy}
                    className="rounded border border-primary bg-primary px-3 py-1 text-csm-sm text-white hover:bg-primary-hover disabled:opacity-60">
              {busy ? t('common.saving', 'Saving...') : t('user_provision.detail.submit_decisions', 'Submit Decisions')}
            </button>
          )}
        </>
      }
    >
      <div>
          <div className="mb-4 grid grid-cols-2 gap-3 rounded border border-csm-border bg-csm-surface-alt px-3 py-2 text-csm-sm">
            <div><span className="text-csm-muted">{t('user_provision.detail.summary.target_user', 'Target user:')}</span> {request.target_display_name}</div>
            <div><span className="text-csm-muted">{t('user_provision.detail.summary.email', 'Email:')}</span> {request.target_email}</div>
            <div><span className="text-csm-muted">{t('user_provision.detail.summary.job', 'Job:')}</span> {request.target_job_label}</div>
            <div><span className="text-csm-muted">{t('user_provision.detail.summary.requested_by', 'Requested by:')}</span> {request.requester_label}</div>
            <div><span className="text-csm-muted">{t('user_provision.detail.summary.step', 'Step:')}</span> {request.current_sequence_order}</div>
            <div><span className="text-csm-muted">{t('user_provision.detail.summary.awaiting', 'Awaiting:')}</span> {request.current_approver_label ?? t('user_provision.detail.summary.finalized', '(finalized)')}</div>
          </div>

          {request.business_reason && (
            <div className="mb-4">
              <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('user_provision.detail.business_reason', 'Business Reason')}</label>
              <div className="rounded border border-csm-border bg-white px-2 py-1 text-csm-sm">
                {request.business_reason}
              </div>
            </div>
          )}

          {/* Roles - editable per-role only when it's my turn */}
          <div className="mb-4">
            <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('user_provision.detail.requested_roles', 'Requested Roles')}</label>
            <div className="overflow-hidden rounded border border-csm-border">
              <table className="w-full text-csm-sm">
                <thead className="bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
                  <tr>
                    <th className="px-2 py-1 text-left">{t('user_provision.detail.roles.col_role', 'Role')}</th>
                    <th className="px-2 py-1 text-left">{t('user_provision.detail.roles.col_status', 'Status')}</th>
                    {isMyTurn && <th className="px-2 py-1 text-left">{t('user_provision.detail.roles.col_my_decision', 'My Decision')}</th>}
                  </tr>
                </thead>
                <tbody>
                  {roles.map(r => (
                    <tr key={r.line_internal_code} className="border-t border-csm-border-soft">
                      <td className="px-2 py-1">
                        <div>{r.role_label}</div>
                        {/* The resolved role name is the line above; this is the role's stable
                            code, shown deliberately as secondary context so an approver can tie
                            the request to the code they administer by. */}
                        <div className="font-mono text-csm-2xs text-csm-muted">{/* fk-display-ok: resolved name shown above */ r.line_internal_code}</div>
                      </td>
                      <td className="px-2 py-1">
                        <RoleStatusBadge code={r.status_internal_code} />
                      </td>
                      {isMyTurn && (
                        <td className="px-2 py-1">
                          {r.status_internal_code === 'PENDING' ? (
                            <div className="inline-flex overflow-hidden rounded border border-csm-border">
                              <button
                                onClick={() => setRoleDecision(r.line_internal_code, 'APPROVE')}
                                className={
                                  'px-2 py-0.5 text-csm-xs ' +
                                  (r.current_decision === 'APPROVE'
                                    ? 'bg-csm-success text-white'
                                    : 'bg-white text-csm-ink-soft hover:bg-csm-surface-alt')
                                }
                              >{t('user_provision.detail.decision.approve', 'Approve')}</button>
                              <button
                                onClick={() => setRoleDecision(r.line_internal_code, 'REJECT')}
                                className={
                                  'border-l border-csm-border px-2 py-0.5 text-csm-xs ' +
                                  (r.current_decision === 'REJECT'
                                    ? 'bg-csm-danger text-white'
                                    : 'bg-white text-csm-ink-soft hover:bg-csm-surface-alt')
                                }
                              >{t('user_provision.detail.decision.reject', 'Reject')}</button>
                            </div>
                          ) : (
                            <span className="text-csm-xs italic text-csm-watermark">{t('user_provision.detail.already_finalized', 'already finalized')}</span>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Sign-off history */}
          <div className="mb-4">
            <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('user_provision.detail.approval_history', 'Approval History')}</label>
            <div className="space-y-2">
              {signoffs.map(s => (
                <div key={s.signoff_id} className="rounded border border-csm-border bg-white px-3 py-2 text-csm-sm">
                  <div className="mb-1 flex items-center gap-2">
                    <span className={
                      'rounded border px-1.5 py-0.5 text-csm-2xs ' +
                      (s.sequence_order === 0
                        ? 'border-csm-info-border bg-csm-info-soft text-csm-info uppercase tracking-wider'
                        : 'border-csm-border bg-csm-surface-alt')
                    }>
                      {s.sequence_order === 0 ? t('user_provision.detail.history.manager', 'Manager') : t('user_provision.detail.history.step', 'Step {{n}}', { n: s.sequence_order })}
                    </span>
                    <span className="font-medium">{s.signed_by_label}</span>
                    <span className="text-csm-muted">{new Date(s.signed_at).toLocaleString()}</span>
                  </div>
                  <ul className="ml-2 mb-1 list-disc pl-3">
                    {s.per_role.map((p, i) => (
                      <li key={i}>
                        <span className={p.decision === 'APPROVE' ? 'text-csm-success' : 'text-csm-danger'}>
                          {p.decision}
                        </span>
                        <span className="ml-1">{p.role_label}</span>
                      </li>
                    ))}
                  </ul>
                  {s.decision_comment && (
                    <div className="text-csm-ink-soft">"{s.decision_comment}"</div>
                  )}
                </div>
              ))}
              {signoffs.length === 0 && (
                <div className="text-csm-sm italic text-csm-watermark">{t('user_provision.detail.history.empty', 'No approvals recorded yet.')}</div>
              )}
            </div>
          </div>

          {isMyTurn && (
            <div className="mb-3">
              <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('user_provision.detail.comment_label', 'Comment (optional)')}</label>
              <textarea rows={2} value={comment} onChange={e => setComment(e.target.value)}
                        className="w-full rounded border border-csm-border px-2 py-1 text-csm-sm focus:border-csm-watermark focus:outline-none" />
            </div>
          )}

          {error && (
            <div className="rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-1 text-csm-sm text-csm-danger">{error}</div>
          )}
      </div>
    </Modal>
  );
}

// =============================================================================
// Badges
// =============================================================================

// Workflow-status → badge variant (label comes localized from
// csm_workflow_status_master_tl).
const PROVISION_STATUS_VARIANT: Record<string, StatusVariant> = {
  APPROVED:  'success',
  REJECTED:  'danger',
  CANCELLED: 'neutral',
  PENDING:   'warning',
};

function ProvisionStatusBadge({ code, label }: { code: string; label: string }) {
  return <StatusBadge variant={PROVISION_STATUS_VARIANT[code] ?? 'neutral'} label={label} />;
}

function RoleStatusBadge({ code }: { code: 'PENDING' | 'APPROVED' | 'REJECTED' }) {
  const { t } = useTranslation();
  const label =
    code === 'APPROVED' ? t('approval.role_status.approved') :
    code === 'REJECTED' ? t('approval.role_status.rejected') :
                          t('approval.role_status.pending');
  return <StatusBadge variant={PROVISION_STATUS_VARIANT[code] ?? 'neutral'} label={label} />;
}
